import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAccount, useSendTransaction, useChainId, useBalance } from "wagmi";
import { trackWalletConnected, trackWalletDisconnected, trackAssetChanged, trackTransferReviewed, trackTransferSubmitted, trackTransferResumed } from "../analytics.ts";
import { isAddress } from "viem";
import { ConnectButton } from "@rainbow-me/rainbowkit";
import { useTransfer } from "../hooks/useTransfer.ts";
import { ENTER_ASSETS, EXIT_ASSETS } from "../swapkit/helpers.ts";
import {
  type TransferStage,
  TRANSFER_STAGE_LABELS,
  hasChainCommitment,
} from "../lit/operation.ts";
import { loadOperation } from "../lit/operation.ts";
import type { EVMTransactionRequest } from "../lit/account.ts";
import { TokenInputBlock, TokenDisplayBlock } from "./ui/TokenAmountBlock.tsx";
import { SwitchButton } from "./ui/SwitchButton.tsx";
import { AssetSelector, getSymbol } from "./ui/AssetSelector.tsx";
import { DestinationRow } from "./ui/DestinationRow.tsx";

/** Rough ETH price used for crypto↔USD display conversion. */
const ETH_DISPLAY_PRICE = 3500;

const CHAIN_EXPLORER: Record<string, string> = {
  "8453": "https://basescan.org/address/",
  "1": "https://etherscan.io/address/",
  "42161": "https://arbiscan.io/address/",
  "137": "https://polygonscan.com/address/",
  "10": "https://optimistic.etherscan.io/address/",
  "56": "https://bscscan.com/address/",
};

const VISIBLE_STEPS: Array<{
  label: string;
  stages: TransferStage[];
  estimatedSeconds: number;
}> = [
  {
    label: "Sending funds",
    stages: ["initiating", "swap_quoting", "swap_signing", "swap_confirming", "deposit_arriving"],
    estimatedSeconds: 120,
  },
  {
    label: "Breaking the trace",
    stages: ["shielding", "shield_confirming"],
    estimatedSeconds: 300,
  },
  {
    label: "Preparing delivery",
    stages: ["exit_preparing"],
    estimatedSeconds: 180,
  },
  {
    label: "Delivering to destination",
    stages: ["unshielding", "exit_confirming"],
    estimatedSeconds: 600,
  },
];

function getVisibleStepIndex(stage: TransferStage | null): number {
  if (!stage) return -1;
  for (let i = 0; i < VISIBLE_STEPS.length; i++) {
    if (VISIBLE_STEPS[i].stages.includes(stage)) return i;
  }
  return -1;
}

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}:${s.toString().padStart(2, "0")}` : `0:${s.toString().padStart(2, "0")}`;
}

function useStepTimer(activeStep: number) {
  const [elapsed, setElapsed] = useState(0);
  const prevStepRef = useRef(activeStep);

  useEffect(() => {
    if (activeStep !== prevStepRef.current) {
      setElapsed(0);
      prevStepRef.current = activeStep;
    }
  }, [activeStep]);

  useEffect(() => {
    if (activeStep < 0) return;
    setElapsed(0);
    const id = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(id);
  }, [activeStep]);

  return elapsed;
}

function getExplorerUrl(address: string, destAsset?: string): string | null {
  if (!destAsset) return CHAIN_EXPLORER["8453"] ? `${CHAIN_EXPLORER["8453"]}${address}` : null;
  const chain = destAsset.split(".")[0];
  const chainIdMap: Record<string, string> = {
    BASE: "8453", ETH: "1", ARB: "42161", MATIC: "137", OP: "10", BSC: "56",
  };
  const cid = chainIdMap[chain] ?? "8453";
  const base = CHAIN_EXPLORER[cid];
  return base ? `${base}${address}` : null;
}

export function TransferPage() {
  const navigate = useNavigate();
  const { address, isConnected, connector } = useAccount();
  const chainId = useChainId();
  const { sendTransactionAsync } = useSendTransaction();
  const [sourceAsset, setSourceAsset] = useState(ENTER_ASSETS[0]!);
  // Pass token address so useBalance returns ERC-20 balance for USDC etc.
  const { data: walletBalance } = useBalance({ address, chainId, token: sourceAsset.tokenAddress });
  const transfer = useTransfer();

  const [inputMode, setInputMode] = useState<"crypto" | "usd">("crypto");
  const [cryptoAmount, setCryptoAmount] = useState("");
  const [usdAmount, setUsdAmount] = useState("");
  const [destAddress, setDestAddress] = useState("");

  // Pre-fill destination with connected wallet address on mount / connect
  useEffect(() => {
    if (address) setDestAddress((prev) => prev || address);
  }, [address]);

  // Track wallet connect / disconnect
  const prevConnectedRef = useRef(false);
  useEffect(() => {
    if (isConnected && !prevConnectedRef.current) {
      trackWalletConnected({ connector: connector?.name ?? "unknown" });
    } else if (!isConnected && prevConnectedRef.current) {
      trackWalletDisconnected();
    }
    prevConnectedRef.current = isConnected;
  }, [isConnected, connector]);
  const [busy, setBusy] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Destination asset always mirrors source asset
  const destAsset = EXIT_ASSETS.find((a) => a.asset === sourceAsset.asset) ?? EXIT_ASSETS[0]!;

  // USD price per token: USDC is a stablecoin ($1), everything else uses ETH_DISPLAY_PRICE
  const assetUsdPrice = sourceAsset.asset.includes("USDC") ? 1 : ETH_DISPLAY_PRICE;

  // Crypto amount used for the actual transfer
  const amount =
    inputMode === "crypto"
      ? cryptoAmount
      : usdAmount
        ? (parseFloat(usdAmount) / assetUsdPrice).toFixed(6)
        : "";

  const [showAbandonConfirm, setShowAbandonConfirm] = useState(false);
  const [activeDestAddress, setActiveDestAddress] = useState<string | null>(null);

  // Per-asset beta limits defined in helpers.ts alongside the asset
  const BETA_MIN_AMOUNT = sourceAsset.minAmount;
  const BETA_MAX_AMOUNT = sourceAsset.maxAmount;
  const parsedAmount = parseFloat(amount);
  const amountBelowMin = !isNaN(parsedAmount) && parsedAmount > 0 && parsedAmount < BETA_MIN_AMOUNT;
  const amountExceedsLimit = !isNaN(parsedAmount) && parsedAmount > BETA_MAX_AMOUNT;
  const amountOutOfRange = amountBelowMin || amountExceedsLimit;

  const walletBalanceNum = walletBalance ? parseFloat(walletBalance.formatted) : null;
  const insufficientBalance =
    walletBalanceNum !== null && !isNaN(parsedAmount) && parsedAmount > 0 && parsedAmount > walletBalanceNum;

  // Unified input error — shown in fixed zone below the stacked blocks
  const inputError: string | undefined = insufficientBalance
    ? "Insufficient balance"
    : amountBelowMin
      ? `Minimum ${BETA_MIN_AMOUNT} ${getSymbol(sourceAsset.asset)} per transfer`
      : amountExceedsLimit
        ? `Beta limit: max ${BETA_MAX_AMOUNT} ${getSymbol(sourceAsset.asset)} per transfer`
        : destAddress && !isAddress(destAddress)
          ? "Invalid address"
          : undefined;

  // Computed display equivalents
  const cryptoAmountNum = parseFloat(amount);
  const usdEquivalent = !isNaN(cryptoAmountNum) && cryptoAmountNum > 0
    ? (cryptoAmountNum * assetUsdPrice).toFixed(2)
    : "";
  const cryptoEquivalent = !isNaN(parseFloat(usdAmount)) && parseFloat(usdAmount) > 0
    ? parseFloat((parseFloat(usdAmount) / assetUsdPrice).toFixed(6)).toString()
    : "";

  useEffect(() => {
    if (transfer.pendingEvidence?.destinationAddress) {
      setActiveDestAddress(transfer.pendingEvidence.destinationAddress);
    }
  }, [transfer.pendingEvidence]);

  const signAndSend = async (evmTx: EVMTransactionRequest): Promise<string> => {
    return sendTransactionAsync({
      chainId,
      to: evmTx.to as `0x${string}`,
      value: evmTx.value ? BigInt(evmTx.value) : 0n,
      data: evmTx.data as `0x${string}`,
      gas: evmTx.gas ? BigInt(evmTx.gas) : undefined,
    });
  };

  async function handleStart() {
    if (!address || !amount || busy) return;
    if (destAddress && !isAddress(destAddress)) return;

    trackTransferSubmitted({ asset: sourceAsset.asset, network: sourceAsset.asset.split(".")[0] ?? sourceAsset.asset });
    setBusy(true);
    setActiveDestAddress(destAddress);
    try {
      await transfer.start({
        sourceAsset: sourceAsset.asset,
        sourceAddress: address,
        amount,
        destinationAsset: destAsset.asset,
        destinationAddress: destAddress,
        signAndSend,
      });
    } catch {
      // error is surfaced via transfer.error
    } finally {
      setBusy(false);
    }
  }

  async function handleRetry() {
    if (transfer.pendingEvidence?.sourceAsset) {
      trackTransferResumed({ asset: transfer.pendingEvidence.sourceAsset });
    }
    setBusy(true);
    if (transfer.pendingEvidence?.destinationAddress) {
      setActiveDestAddress(transfer.pendingEvidence.destinationAddress);
    }
    try {
      await transfer.retry(signAndSend);
    } catch {
      // error is surfaced via transfer.error
    } finally {
      setBusy(false);
    }
  }

  function handleReset(force = false) {
    transfer.reset(force);
    setShowAbandonConfirm(false);
    setShowConfirm(false);
    setCryptoAmount("");
    setUsdAmount("");
    setDestAddress(address ?? "");
  }

  function handleAbandon() {
    const op = loadOperation();
    if (op && hasChainCommitment(op)) {
      setShowAbandonConfirm(true);
    } else {
      handleReset();
    }
  }

  const MAIN_CARD_CLASS = "rounded-3xl p-4 md:p-6 w-full flex flex-col md:min-h-[533px]";

  const showConfigure = !showConfirm && !transfer.stage && !transfer.hasPendingTransfer;
  const showConfirmation = showConfirm && !transfer.stage;
  const showResume = !showConfirm && !transfer.stage && transfer.hasPendingTransfer && transfer.stage !== "failed";
  const showProcessing = transfer.isProcessing;
  const showComplete = transfer.stage === "completed";
  const showFailed = transfer.stage === "failed";

  const activeVisibleStep = getVisibleStepIndex(transfer.stage);
  const stepElapsed = useStepTimer(activeVisibleStep);

  return (
    <div className="flex items-start justify-center p-4 md:p-8">
      <div className="w-full mt-4 max-w-120">

        {/* Phase 1: Configure */}
        {showConfigure && (
          <div className={isConnected ? MAIN_CARD_CLASS : "rounded-3xl p-4 md:p-6 w-full flex flex-col"} style={{ background: "#122134" }}>
            {/* Card header — centered */}
            <div className="text-center mb-8 md:mb-12">
              <h2 className="font-heading font-bold text-ghost-white text-xl">
                Private Transfer
              </h2>
              <p className="text-xs text-mist mt-0.5 italic">Anywhere Untraceable</p>
            </div>

            {!isConnected && (
              <div className="flex justify-center py-8">
                <ConnectButton.Custom>
                  {({ openConnectModal, mounted }) => (
                    <button
                      onClick={openConnectModal}
                      disabled={!mounted}
                      className="w-full py-3.5 rounded-xl text-white font-heading text-sm font-semibold hover:brightness-110 transition-all"
                      style={{ background: "#3b83da" }}
                    >
                      Connect Wallet
                    </button>
                  )}
                </ConnectButton.Custom>
              </div>
            )}

            {isConnected && (() => {
              const symbol = getSymbol(sourceAsset.asset);
              const topSymbol = inputMode === "crypto" ? symbol : "USD";
              const bottomUnit = inputMode === "crypto" ? "USD" : symbol;
              const walletBalanceNum = walletBalance
                ? parseFloat(walletBalance.formatted).toFixed(4)
                : undefined;

              return (
                <div className="flex flex-col flex-1">
                  {/* Stacked blocks — asset badge overlaps the top edge of the top card */}
                  <div className="mb-3">
                    <TokenInputBlock
                      symbol={topSymbol}
                      amount={inputMode === "crypto" ? cryptoAmount : usdAmount}
                      onAmountChange={(v) =>
                        inputMode === "crypto" ? setCryptoAmount(v) : setUsdAmount(v)
                      }
                      walletBalance={walletBalanceNum}
                      onFillBalance={() => {
                        if (!walletBalance) return;
                        const raw = parseFloat(walletBalance.formatted);
                        if (inputMode === "crypto") {
                          setCryptoAmount(raw.toFixed(6));
                        } else {
                          setUsdAmount((raw * assetUsdPrice).toFixed(2));
                        }
                      }}
                      onFillMin={() => {
                        if (inputMode === "crypto") {
                          setCryptoAmount(String(BETA_MIN_AMOUNT));
                        } else {
                          setUsdAmount((BETA_MIN_AMOUNT * assetUsdPrice).toFixed(2));
                        }
                      }}
                      onFillMax={() => {
                        if (inputMode === "crypto") {
                          setCryptoAmount(String(BETA_MAX_AMOUNT));
                        } else {
                          setUsdAmount((BETA_MAX_AMOUNT * assetUsdPrice).toFixed(2));
                        }
                      }}
                      badge={
                        <AssetSelector
                          assets={ENTER_ASSETS}
                          selected={sourceAsset}
                          onChange={(a) => {
                            setSourceAsset(a);
                            setCryptoAmount("");
                            setUsdAmount("");
                            trackAssetChanged({ asset: a.asset });
                          }}
                        />
                      }
                    />
                    {/* Switch button — sits between the two stacked cards, vertically centered at the seam */}
                    <div className="flex justify-center relative z-20 -my-5">
                      <SwitchButton onClick={() => {
                        if (inputMode === "crypto") {
                          if (usdEquivalent) setUsdAmount(usdEquivalent);
                          setInputMode("usd");
                        } else {
                          if (cryptoEquivalent) setCryptoAmount(parseFloat(cryptoEquivalent).toString());
                          setInputMode("crypto");
                        }
                      }} />
                    </div>
                    {/* Bottom block: narrower than top + pulled 8px up so top corners hide behind top card */}
                    <div className="mx-3 -mt-2 relative z-0">
                      <TokenDisplayBlock
                        value={inputMode === "crypto" ? usdEquivalent : cryptoEquivalent}
                        unit={bottomUnit}
                      />
                    </div>
                  </div>

                  {/* Fixed-height error zone — always reserves space so no layout shift */}
                  <div className="min-h-[1.25rem] mt-1.5 mb-1">
                    {inputError && (
                      <p className="text-blaze text-xs px-1">{inputError}</p>
                    )}
                  </div>

                  {/* Destination row */}
                  <DestinationRow
                    connectedAddress={address ?? ""}
                    value={destAddress}
                    onChange={setDestAddress}
                  />

                  <p className="text-[0.7rem] text-ash/40 mt-3 text-center leading-relaxed">
                    Beta — supported chains and tokens are limited. More coming soon.
                  </p>

                  <button
                    onClick={() => { trackTransferReviewed({ asset: sourceAsset.asset, amount }); setShowConfirm(true); }}
                    disabled={busy || !amount || !destAddress || !isAddress(destAddress) || amountOutOfRange || insufficientBalance}
                    className="w-full mt-auto py-3.5 rounded-xl text-white font-heading text-sm font-semibold hover:brightness-110 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ background: "#3b83da" }}
                  >
                    Review Transfer
                  </button>
                </div>
              );
            })()}
          </div>
        )}

        {/* Confirmation */}
        {showConfirmation && (
          <div className={MAIN_CARD_CLASS} style={{ background: "#122134" }}>
            <h2 className="font-heading text-xl font-semibold text-ghost-white mb-4 md:mb-7 text-center">
              Review Transfer
            </h2>

            <div className="space-y-0 mb-3 md:mb-5 border border-border/60 rounded-lg overflow-hidden">
              <SummaryRow label="Send" value={`${amount} ${sourceAsset.label}`} />
              <SummaryRow label="Destination" value={truncate(destAddress, 12)} mono />
              <SummaryRow label="Receive as" value={destAsset.label} />
            </div>

            <BetaWarningTooltip maxAmount={BETA_MAX_AMOUNT} assetLabel={sourceAsset.label} />

            <div className="rounded-lg bg-border/30 border border-border/60 px-3 py-3 mb-4">
              <p className="text-sm font-heading font-medium text-ghost-white mb-1.5">
                Slippage & costs
              </p>
              <div className="space-y-1 text-xs text-mist">
                <div className="flex justify-between">
                  <span>Expected slippage</span>
                  <span className="text-ghost-white font-heading">&lt; 2%</span>
                </div>
                <div className="flex justify-between">
                  <span>Max possible slippage</span>
                  <span className="text-ghost-white font-heading">~5.9%</span>
                </div>
                <div className="flex justify-between">
                  <span>Typical total cost</span>
                  <span className="text-ghost-white font-heading">2–4%</span>
                </div>
              </div>
              <SlippageTooltip />
            </div>

            <div className="flex gap-2.5 mt-auto">
              <button
                onClick={() => setShowConfirm(false)}
                className="px-5 py-3.5 border border-border rounded-xl text-mist font-heading text-sm hover:border-mist transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleStart}
                disabled={busy}
                className="flex-1 py-3.5 rounded-xl text-white font-heading text-sm font-semibold hover:brightness-110 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: "#3b83da" }}
              >
                {busy ? "Processing..." : "Confirm and Transfer"}
              </button>
            </div>
          </div>
        )}

        {/* Resume prompt */}
        {showResume && (
          <div className="rounded-3xl p-4 md:p-6" style={{ background: "#122134" }}>
            <h2 className="font-heading text-xl font-semibold text-ghost-white mb-2">
              Interrupted Transfer
            </h2>
            <p className="text-sm text-ash mb-6 leading-relaxed">
              A previous transfer was interrupted. You can resume it or abandon it.
            </p>

            {transfer.pendingEvidence && (
              <div className="flex flex-col gap-1 mb-6 px-3 py-2.5 rounded-lg border border-border/80 font-mono text-[0.72rem] text-ash" style={{ background: "#0e1a2a" }}>
                <span>Amount: {transfer.pendingEvidence.requestedAmount} {transfer.pendingEvidence.sourceAsset}</span>
                <span>To: {truncate(transfer.pendingEvidence.destinationAddress ?? "", 12)}</span>
              </div>
            )}

            <div className="flex gap-2.5">
              <button
                onClick={handleAbandon}
                className="px-5 py-3.5 border border-border rounded-xl text-mist font-heading text-sm hover:border-mist transition-colors"
              >
                Abandon
              </button>
              <button
                onClick={handleRetry}
                disabled={busy || !isConnected}
                className="flex-1 py-3.5 rounded-xl text-white font-heading text-sm font-semibold hover:brightness-110 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: "#3b83da" }}
              >
                {busy ? "Resuming..." : "Resume Transfer"}
              </button>
            </div>
          </div>
        )}

        {/* Phase 2: Processing */}
        {showProcessing && (
          <div className="rounded-2xl p-8" style={{ background: "#122134" }}>
            <h2 className="font-heading text-xl font-semibold text-ghost-white mb-6 text-center">
              Transfer in Progress
            </h2>

            {/* Stepper */}
            <div className="space-y-0">
              {VISIBLE_STEPS.map((step, i) => {
                const isActive = i === activeVisibleStep;
                const isComplete = activeVisibleStep > i;
                const isPending = activeVisibleStep < i;
                const overTime = isActive && stepElapsed > step.estimatedSeconds;
                const estMin = Math.ceil(step.estimatedSeconds / 60);

                return (
                  <div key={step.label} className="flex items-start gap-3 relative">
                    {/* Connector line */}
                    {i < VISIBLE_STEPS.length - 1 && (
                      <div
                        className={`absolute left-[11px] top-[24px] w-0.5 h-[calc(100%)] ${
                          isComplete ? "bg-emerald-500/40" : "bg-[#0e1a2a]"
                        }`}
                      />
                    )}

                    {/* Icon */}
                    <div className="relative z-10 shrink-0 mt-0.5">
                      {isComplete && (
                        <div className="w-[22px] h-[22px] rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path d="M2.5 6.5L5 9L9.5 3.5" stroke="rgb(52,211,153)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      )}
                      {isActive && (
                        <div className="w-[22px] h-[22px] rounded-full bg-spectral-violet/15 border border-spectral-violet/40 flex items-center justify-center">
                          <div className="w-2 h-2 rounded-full bg-spectral-violet animate-pulse-dot" />
                        </div>
                      )}
                      {isPending && (
                        <div className="w-[22px] h-[22px] rounded-full border border-white/15" style={{ background: "#0e1a2a" }} />
                      )}
                    </div>

                    {/* Label + timer */}
                    <div className={`pb-5 flex-1 ${isActive ? "text-ghost-white" : isComplete ? "text-mist" : "text-ash/50"}`}>
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="font-heading text-sm font-medium">
                          {step.label}
                        </span>
                        {isActive && (
                          <span className="font-mono text-xs tabular-nums shrink-0" style={{ color: "#5d6a7b" }}>
                            {formatElapsed(stepElapsed)}{" "}
                            <span>/ ~{estMin}m</span>
                          </span>
                        )}
                        {isPending && (
                          <span className="font-mono text-[0.65rem] text-ash/40 shrink-0">~{estMin}m</span>
                        )}
                      </div>
                      {isActive && transfer.stage && !overTime && (
                        <div className="text-xs text-spectral-violet mt-0.5">
                          {TRANSFER_STAGE_LABELS[transfer.stage]}
                        </div>
                      )}
                      {overTime && (
                        <div className="text-xs text-amber-400/90 mt-0.5">
                          Still processing — your funds are secure
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {transfer.evidence?.expectedReceiveAmount && activeVisibleStep >= 3 ? (
              <div className="mt-4 px-4 py-3 rounded-lg border border-emerald-500/15" style={{ background: "#122134" }}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-heading" style={{ color: "#5d6a7b" }}>You will receive</span>
                  <span className="text-sm text-ghost-white font-heading">~{parseFloat(transfer.evidence.expectedReceiveAmount).toFixed(6)} {formatAssetName(transfer.evidence.destinationAsset)}</span>
                </div>
                {transfer.evidence.destinationAddress && (() => {
                  const addr = transfer.evidence.destinationAddress!;
                  const explorerUrl = getExplorerUrl(addr, transfer.evidence.destinationAsset);
                  const truncated = `${addr.slice(0, 8)}...${addr.slice(-6)}`;
                  return (
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-sm font-heading" style={{ color: "#5d6a7b" }}>Destination</span>
                      {explorerUrl ? (
                        <a href={explorerUrl} target="_blank" rel="noopener noreferrer"
                          className="font-mono text-xs text-ghost-white/70 hover:text-ghost-white transition-colors">
                          {truncated}
                        </a>
                      ) : (
                        <span className="font-mono text-xs text-ghost-white/70">{truncated}</span>
                      )}
                    </div>
                  );
                })()}
              </div>
            ) : activeDestAddress ? (
              <div className="mt-4 px-4 py-3 rounded-lg border border-white/8" style={{ background: "#122134" }}>
                {(() => {
                  const explorerUrl = getExplorerUrl(activeDestAddress, destAsset.asset);
                  const truncated = `${activeDestAddress.slice(0, 8)}...${activeDestAddress.slice(-6)}`;
                  return (
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-heading" style={{ color: "#5d6a7b" }}>Destination</span>
                      {explorerUrl ? (
                        <a href={explorerUrl} target="_blank" rel="noopener noreferrer"
                          className="font-mono text-xs text-ghost-white/70 hover:text-ghost-white transition-colors">
                          {truncated}
                        </a>
                      ) : (
                        <span className="font-mono text-xs text-ghost-white/70">{truncated}</span>
                      )}
                    </div>
                  );
                })()}
              </div>
            ) : null}

            {transfer.stage === "swap_signing" && (
              <div className="mt-3 px-4 py-3 rounded-lg border border-white/8 text-sm text-mist font-heading text-center" style={{ background: "#122134" }}>
                Check your wallet for a signature request
              </div>
            )}
          </div>
        )}

        {/* Phase 3: Complete */}
        {showComplete && (
          <div className={MAIN_CARD_CLASS} style={{ background: "#122134" }}>
            <div className="flex flex-col items-center text-center mb-4 md:mb-6">
              <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-3 md:mb-5">
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                  <path d="M6 14.5L11.5 20L22 8" stroke="rgb(52,211,153)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>

              <h2 className="font-heading text-xl font-semibold text-ghost-white mb-1">
                Transfer Complete
              </h2>
              <p className="text-sm text-mist leading-relaxed">
                Funds delivered with no on-chain link to your source wallet.
              </p>
            </div>

            {transfer.evidence?.expectedReceiveAmount ? (
              <div className="space-y-0 mb-6 border border-border/60 rounded-lg overflow-hidden">
                <SummaryRow
                  label="Sent"
                  value={`${transfer.evidence.requestedAmount} ${formatAssetName(transfer.evidence.sourceAsset)}`}
                />
                <SummaryRow
                  label="Estimated received"
                  value={`~${transfer.evidence.expectedReceiveAmount} ${formatAssetName(transfer.evidence.destinationAsset)}`}
                />
                <SummaryRow
                  label="Minimum after slippage"
                  value={`${transfer.evidence.expectedReceiveAmountMin} ${formatAssetName(transfer.evidence.destinationAsset)}`}
                />
                {transfer.evidence.exitSlippageBps != null && (
                  <SummaryRow
                    label="Slippage"
                    value={`${(transfer.evidence.exitSlippageBps / 100).toFixed(1)}%`}
                  />
                )}
                <SummaryRow
                  label="Destination"
                  value={truncate(transfer.evidence.destinationAddress ?? "", 12)}
                  mono
                />
              </div>
            ) : (
              <p className="text-sm text-mist mb-6 text-center leading-relaxed">
                No detailed summary available for this transfer.
              </p>
            )}

            <button
              onClick={() => handleReset()}
              className="w-full mt-auto py-3.5 rounded-xl text-white font-heading text-sm font-semibold hover:brightness-110 transition-all"
              style={{ background: "#3b83da" }}
            >
              Start New Transfer
            </button>
          </div>
        )}

        {/* Failed state */}
        {showFailed && (
          <div className="rounded-3xl p-4 md:p-6 w-full flex flex-col" style={{ background: "#122134" }}>
            <h2 className="font-heading text-xl font-semibold text-ghost-white mb-2 text-center">
              Transfer Failed
            </h2>

            {transfer.error && (
              <div className="px-3 py-2.5 rounded-lg bg-blaze/[0.08] border border-blaze/20 text-xs text-blaze font-heading mb-5 break-all">
                {transfer.error}
              </div>
            )}

            <div className="flex gap-2.5 mt-4">
              <button
                onClick={handleAbandon}
                className="px-5 py-3.5 border border-border rounded-xl text-mist font-heading text-sm hover:border-mist transition-colors"
              >
                Abandon
              </button>
              <button
                onClick={handleRetry}
                disabled={busy || !isConnected}
                className="flex-1 py-3.5 rounded-xl text-white font-heading text-sm font-semibold hover:brightness-110 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: "#3b83da" }}
              >
                {busy ? "Retrying..." : "Retry"}
              </button>
            </div>

            <button
              onClick={() => navigate("/app/recovery")}
              className="w-full mt-3 py-2.5 border border-border rounded-xl text-ash font-heading text-sm hover:border-mist hover:text-mist transition-all"
            >
              Recovery Options
            </button>
          </div>
        )}

        {/* Abandon confirmation modal */}
        {showAbandonConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-void/80 backdrop-blur-sm p-4">
            <div className="bg-surface border border-blaze/30 rounded-2xl p-8 max-w-100 w-full">
              <h3 className="font-heading text-lg font-semibold text-ghost-white mb-3">
                Funds May Be Lost
              </h3>
              <p className="text-sm text-mist mb-6 leading-relaxed">
                This transfer has funds in the pipeline. Abandoning it may result in
                permanent loss. You can try recovery from the settings page instead.
              </p>
              <div className="flex gap-2.5">
                <button
                  onClick={() => setShowAbandonConfirm(false)}
                  className="flex-1 py-3 border border-border rounded-lg text-mist font-heading text-sm hover:border-mist transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleReset(true)}
                  className="flex-1 py-3 rounded-lg bg-blaze/20 border border-blaze/30 text-blaze font-heading text-sm font-semibold hover:bg-blaze/30 transition-all"
                >
                  Abandon Anyway
                </button>
              </div>
            </div>
          </div>
        )}
        <a
          href="mailto:espectrosupport@proton.me"
          target="_blank"
          rel="noopener noreferrer"
          className="block text-center text-xs text-ash/40 hover:text-ash/70 transition-colors font-body mt-4 mb-2"
        >
          Support
        </a>
      </div>
    </div>
  );
}

function truncate(str: string, chars = 8) {
  if (!str) return "";
  if (str.length <= chars * 2 + 3) return str;
  return `${str.slice(0, chars)}...${str.slice(-Math.min(6, chars))}`;
}

function formatAssetName(asset?: string): string {
  if (!asset) return "";
  return asset.includes(".") ? asset.split(".")[1]! : asset;
}

function BetaWarningTooltip({ maxAmount, assetLabel }: { maxAmount: number; assetLabel: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="relative rounded-lg border border-exit-orange/20 px-3 py-2.5 mb-3 cursor-default"
      style={{ background: "#0e1a2a" }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        className="flex items-center justify-between w-full text-left"
        onClick={() => setOpen((v) => !v)}
        type="button"
      >
        <p className="text-sm font-heading font-semibold text-exit-orange">
          Beta software — use at your own risk
        </p>
      </button>

      {open && (
        <div
          className="absolute top-full mt-1.5 left-0 right-0 rounded-lg border border-exit-orange/20 px-3 py-2.5 text-xs text-mist leading-relaxed z-30 shadow-xl"
          style={{ background: "#0e1a2a" }}
        >
          Espectro is in active development. Supported chains and tokens are limited — more coming soon. During beta, transfers are capped at {maxAmount} {assetLabel} to limit exposure while the system is being hardened.
        </div>
      )}
    </div>
  );
}

function SlippageTooltip() {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="relative flex justify-start mt-2"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        className="flex items-center gap-1 text-[0.68rem] text-ash/60 hover:text-ash transition-colors"
        onClick={() => setOpen((v) => !v)}
        type="button"
      >
        <svg width="11" height="11" viewBox="0 0 12 12" fill="none" className="shrink-0">
          <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.2" />
          <path d="M6 5.5V8.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
          <circle cx="6" cy="3.75" r="0.6" fill="currentColor" />
        </svg>
        How does it work?
      </button>

      {open && (
        <div
          className="absolute bottom-full mb-2 left-0 w-64 rounded-xl border border-white/8 px-3 py-2.5 text-xs text-ash leading-relaxed shadow-xl z-30"
          style={{ background: "#0d1625" }}
        >
          Each transfer involves two cross-chain swaps (enter and exit), each allowing up to 3% slippage. In the worst case both swaps hit maximum slippage simultaneously, reaching ~5.9%. Actual slippage is usually well below this. Route fees and gas are additional but typically small.
        </div>
      )}
    </div>
  );
}

function SummaryRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between items-center px-4 py-1.5 md:py-2.5 border-b border-border/60 last:border-b-0 gap-3">
      <span className="font-heading text-xs text-ash shrink-0">{label}</span>
      <span className={`text-sm text-ghost-white text-right break-all ${mono ? "font-mono" : "font-heading"}`}>
        {value}
      </span>
    </div>
  );
}
